import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../lib/supabase';
import {
  FaShieldAlt, FaCheckCircle, FaTimesCircle, FaSearch,
  FaEye, FaEyeSlash, FaClock, FaUser, FaFileAlt,
  FaExclamationTriangle, FaDownload, FaFilter
} from 'react-icons/fa';

const CreditConsentDashboard = () => {
  const [applications, setApplications] = useState([]);
  const [creditPulls, setCreditPulls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterConsent, setFilterConsent] = useState('all'); // all, consented, no-consent
  const [filterPulled, setFilterPulled] = useState('all'); // all, pulled, not-pulled
  const [expandedRow, setExpandedRow] = useState(null);
  const [showSSN, setShowSSN] = useState({});
  const [stats, setStats] = useState({ total: 0, consented: 0, pulled: 0, pending: 0 });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load all finance applications
      const { data: apps, error: appError } = await supabase
        .from('finance_applications')
        .select('*')
        .order('created_at', { ascending: false });

      if (appError) throw appError;

      // Load all credit reports
      const { data: reports, error: reportError } = await supabase
        .from('credit_reports')
        .select('*')
        .order('created_at', { ascending: false });

      if (reportError) throw reportError;

      setApplications(apps || []);
      setCreditPulls(reports || []);

      // Calculate stats
      const total = (apps || []).length;
      const consented = (apps || []).filter(a => a.credit_check_consent).length;
      const pulled = (reports || []).length;
      const pending = consented - new Set((reports || []).map(r => r.applicant_name)).size;
      setStats({ total, consented, pulled, pending: Math.max(0, pending) });

    } catch (err) {
      console.error('Error loading consent data:', err);
    } finally {
      setLoading(false);
    }
  };

  const maskSSN = (ssn) => {
    if (!ssn) return '—';
    const cleaned = ssn.replace(/\D/g, '');
    if (cleaned.length < 4) return '***-**-****';
    return `***-**-${cleaned.slice(-4)}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit'
    });
  };

  // Match credit pulls to an application by name
  const getPullsForApp = (app) => {
    const fullName = `${app.first_name || ''} ${app.last_name || ''}`.trim().toLowerCase();
    return creditPulls.filter(p => {
      const pullName = (p.applicant_name || '').toLowerCase();
      return pullName === fullName || pullName.includes(fullName) || fullName.includes(pullName);
    });
  };

  // Filter applications
  const filteredApps = applications.filter(app => {
    const name = `${app.first_name || ''} ${app.last_name || ''}`.toLowerCase();
    const email = (app.email || '').toLowerCase();
    const query = searchQuery.toLowerCase();

    const matchesSearch = !query || name.includes(query) || email.includes(query);
    const matchesConsent = filterConsent === 'all' ||
      (filterConsent === 'consented' && app.credit_check_consent) ||
      (filterConsent === 'no-consent' && !app.credit_check_consent);
    const pulls = getPullsForApp(app);
    const matchesPulled = filterPulled === 'all' ||
      (filterPulled === 'pulled' && pulls.length > 0) ||
      (filterPulled === 'not-pulled' && pulls.length === 0);

    return matchesSearch && matchesConsent && matchesPulled;
  });

  const exportCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Consent Given', 'Consent Date', 'Credit Pulled', 'Pull Count', 'Status'];
    const rows = filteredApps.map(app => {
      const pulls = getPullsForApp(app);
      return [
        `${app.first_name || ''} ${app.last_name || ''}`,
        app.email || '',
        app.phone || '',
        app.credit_check_consent ? 'Yes' : 'No',
        formatDate(app.created_at),
        pulls.length > 0 ? 'Yes' : 'No',
        pulls.length,
        app.status || 'pending'
      ].join(',');
    });
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `credit-consent-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading consent records...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <FaShieldAlt className="text-amber-500" />
          Credit Authorization Log
        </h2>
        <p className="text-gray-600 mt-1">Track who authorized credit pulls and their consent status</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <FaFileAlt className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              <p className="text-xs text-gray-500">Total Applications</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <FaCheckCircle className="text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.consented}</p>
              <p className="text-xs text-gray-500">Consent Given</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <FaUser className="text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.pulled}</p>
              <p className="text-xs text-gray-500">Credit Pulls</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <FaClock className="text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.pending}</p>
              <p className="text-xs text-gray-500">Consented, Not Pulled</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 shadow-sm">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative">
            <FaSearch className="absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name or email..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-gray-900 text-sm"
            />
          </div>
          <div className="flex gap-3">
            <select
              value={filterConsent}
              onChange={(e) => setFilterConsent(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-gray-700 text-sm"
            >
              <option value="all">All Consent</option>
              <option value="consented">Consent Given</option>
              <option value="no-consent">No Consent</option>
            </select>
            <select
              value={filterPulled}
              onChange={(e) => setFilterPulled(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-gray-700 text-sm"
            >
              <option value="all">All Pulls</option>
              <option value="pulled">Credit Pulled</option>
              <option value="not-pulled">Not Pulled</option>
            </select>
            <button
              onClick={exportCSV}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors"
            >
              <FaDownload className="w-3 h-3" />
              Export
            </button>
            <button
              onClick={loadData}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Applications Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Applicant</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">SSN</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Consent</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Credit Pulled</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredApps.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-gray-500">
                    {searchQuery || filterConsent !== 'all' || filterPulled !== 'all'
                      ? 'No applications match your filters'
                      : 'No finance applications yet'}
                  </td>
                </tr>
              ) : (
                filteredApps.map((app) => {
                  const pulls = getPullsForApp(app);
                  const isExpanded = expandedRow === app.id;
                  return (
                    <React.Fragment key={app.id}>
                      <tr
                        onClick={() => setExpandedRow(isExpanded ? null : app.id)}
                        className={`border-b border-gray-100 cursor-pointer transition-colors ${isExpanded ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                      >
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-gray-900">{app.first_name} {app.last_name}</p>
                            <p className="text-xs text-gray-500">{app.email}</p>
                            {app.phone && <p className="text-xs text-gray-400">{app.phone}</p>}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-700 font-mono">
                              {showSSN[app.id] ? (app.ssn || '—') : maskSSN(app.ssn)}
                            </span>
                            {app.ssn && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowSSN(prev => ({ ...prev, [app.id]: !prev[app.id] }));
                                }}
                                className="text-gray-400 hover:text-gray-600"
                              >
                                {showSSN[app.id] ? <FaEyeSlash className="w-3 h-3" /> : <FaEye className="w-3 h-3" />}
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {app.credit_check_consent ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                              <FaCheckCircle className="w-3 h-3" /> Authorized
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-100 text-red-700 text-xs font-medium">
                              <FaTimesCircle className="w-3 h-3" /> No Consent
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {formatDate(app.created_at)}
                        </td>
                        <td className="px-4 py-3">
                          {pulls.length > 0 ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-medium">
                              {pulls.length} pull{pulls.length !== 1 ? 's' : ''}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">None</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                            app.status === 'approved' ? 'bg-green-100 text-green-700' :
                            app.status === 'denied' ? 'bg-red-100 text-red-700' :
                            app.status === 'reviewing' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {(app.status || 'pending').charAt(0).toUpperCase() + (app.status || 'pending').slice(1)}
                          </span>
                        </td>
                      </tr>

                      {/* Expanded Row — Pull History */}
                      <AnimatePresence>
                        {isExpanded && (
                          <tr>
                            <td colSpan={6} className="px-0 py-0">
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="overflow-hidden"
                              >
                                <div className="px-8 py-4 bg-blue-50 border-b border-blue-100">
                                  <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                    <FaFileAlt className="text-blue-500" />
                                    Credit Pull History for {app.first_name} {app.last_name}
                                  </h4>

                                  {/* Consent details */}
                                  <div className="mb-3 p-3 bg-white rounded-lg border border-gray-200">
                                    <p className="text-xs text-gray-500 mb-1">Consent Record</p>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                      <div>
                                        <span className="text-gray-500">Credit consent:</span>{' '}
                                        <span className={app.credit_check_consent ? 'text-green-700 font-medium' : 'text-red-700 font-medium'}>
                                          {app.credit_check_consent ? 'Yes' : 'No'}
                                        </span>
                                      </div>
                                      <div>
                                        <span className="text-gray-500">Terms accepted:</span>{' '}
                                        <span className={app.terms_accepted ? 'text-green-700 font-medium' : 'text-red-700 font-medium'}>
                                          {app.terms_accepted ? 'Yes' : 'No'}
                                        </span>
                                      </div>
                                      <div>
                                        <span className="text-gray-500">Application date:</span>{' '}
                                        <span className="text-gray-800">{formatDate(app.created_at)}</span>
                                      </div>
                                      <div>
                                        <span className="text-gray-500">Source:</span>{' '}
                                        <span className="text-gray-800">{app.deal_id ? 'In-person' : 'Online'}</span>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Pull history */}
                                  {pulls.length > 0 ? (
                                    <div className="space-y-2">
                                      {pulls.map((pull, idx) => (
                                        <div key={pull.id || idx} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                                          <div className="flex items-center gap-4">
                                            <div className="text-sm">
                                              <span className="font-medium text-gray-800">{pull.product || pull.report_type || 'Credit'}</span>
                                              <span className="text-gray-400 mx-2">•</span>
                                              <span className="text-gray-600">{pull.bureau || '—'}</span>
                                            </div>
                                          </div>
                                          <div className="flex items-center gap-4">
                                            {pull.score && (
                                              <span className={`font-bold text-lg ${
                                                pull.score >= 720 ? 'text-green-600' :
                                                pull.score >= 640 ? 'text-yellow-600' :
                                                'text-red-600'
                                              }`}>
                                                {pull.score}
                                              </span>
                                            )}
                                            <span className="text-xs text-gray-500">{formatDate(pull.created_at)}</span>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <p className="text-sm text-gray-500 italic">No credit pulls on record for this applicant.</p>
                                  )}

                                  {/* Warning if pulled without consent */}
                                  {pulls.length > 0 && !app.credit_check_consent && (
                                    <div className="mt-3 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                                      <FaExclamationTriangle className="text-red-500" />
                                      <span className="text-sm text-red-700 font-medium">
                                        Warning: Credit was pulled without recorded consent on file.
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </motion.div>
                            </td>
                          </tr>
                        )}
                      </AnimatePresence>
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
          <p className="text-xs text-gray-500">
            Showing {filteredApps.length} of {applications.length} applications
          </p>
          <p className="text-xs text-gray-400">
            Data refreshed {new Date().toLocaleTimeString()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default CreditConsentDashboard;
